import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import SportsSoccerRoundedIcon from "@mui/icons-material/SportsSoccerRounded";
import AppLayout from "../../../components/ui/AppLayout";
import PageHeader from "../../../components/ui/PageHeader";
import SectionCard from "../../../components/ui/SectionCard";
import LoadingState from "../../../components/ui/LoadingState";
import EmptyState from "../../../components/ui/EmptyState";
import TeamCrest from "../../../components/ui/TeamCrest";
import GameCard from "../GameCard";
import api from "../../../api";
import tokens from "../../../theme/tokens";

// Classificação dos grupos, geral + mata-mata. `admin` muda apenas a navegação.
const ClassificacaoPage = ({ admin = true }) => {
  const [classificacao, setClassificacao] = useState({ A: [], B: [] });
  const [classificacaoGeral, setClassificacaoGeral] = useState([]);
  const [semifinais, setSemifinais] = useState([]);
  const [finais, setFinais] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchClassificacao = async () => {
      try {
        const [grupos, geral] = await Promise.all([api.get("/classificacao/grupos"), api.get("/classificacaoGeral")]);
        setClassificacao(grupos.data.classificacao || { A: [], B: [] });
        setClassificacaoGeral(geral.data.classificacaoGeral || []);
        setSemifinais(geral.data.semifinais || []);
        setFinais(geral.data.finais || []);
      } catch (error) {
        console.error("Erro ao buscar a classificação:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClassificacao();
  }, []);

  const renderClassificacao = (grupo, titulo) => (
    <SectionCard title={titulo} contentSx={{ p: 0, "&:last-child": { pb: 0 } }} sx={{ height: "100%" }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Time</TableCell>
              <TableCell align="center">P</TableCell>
              <TableCell align="center">V</TableCell>
              <TableCell align="center">E</TableCell>
              <TableCell align="center">D</TableCell>
              <TableCell align="center">SG</TableCell>
              <TableCell align="center">GP</TableCell>
              <TableCell align="center">GC</TableCell>
              <TableCell align="center" title="Cartões amarelos / azuis">CA/CZ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {grupo.map((time, index) => (
              <TableRow key={time.nome} hover>
                <TableCell sx={{ borderLeft: `3px solid ${index < 2 ? tokens.color.success : "transparent"}`, fontWeight: 700 }}>{index + 1}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <TeamCrest name={time.nome} size={28} />
                    <Typography variant="body2" fontWeight={600} noWrap>{time.nome}</Typography>
                  </Stack>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>{time.pontos}</TableCell>
                <TableCell align="center">{time.vitorias}</TableCell>
                <TableCell align="center">{time.empates}</TableCell>
                <TableCell align="center">{time.derrotas}</TableCell>
                <TableCell align="center" sx={{ color: time.saldoGols > 0 ? "success.main" : time.saldoGols < 0 ? "error.main" : "text.secondary", fontWeight: 600 }}>{time.saldoGols}</TableCell>
                <TableCell align="center">{time.golsPro}</TableCell>
                <TableCell align="center">{time.golsContra}</TableCell>
                <TableCell align="center">{time.cartoesAmarelos}/{time.cartoesAzuis}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ width: 10, height: 10, bgcolor: tokens.color.success, borderRadius: 0.5 }} />
        <Typography variant="caption" color="text.secondary">Classificados para a semifinal</Typography>
      </Box>
    </SectionCard>
  );

  const renderGames = (title, games, empty) => (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>{title}</Typography>
      {games.length > 0 ? (
        <Grid container spacing={2}>
          {games.map((game) => (
            <Grid item xs={12} md={6} key={game._id}>
              <GameCard jogo={game} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <SectionCard><EmptyState title={empty} /></SectionCard>
      )}
    </Box>
  );

  return (
    <AppLayout>
      <PageHeader
        backTo={admin ? "/torneio" : "/squad"}
        eyebrow="Torneio"
        title="Classificação"
        actions={<Button variant="outlined" startIcon={<SportsSoccerRoundedIcon />} onClick={() => navigate(admin ? "/listar-jogos" : "/jogos")}>Ver jogos</Button>}
      />
      {isLoading ? (
        <LoadingState label="Carregando classificação..." />
      ) : (
        <>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>{renderClassificacao(classificacao.A || [], "Grupo A")}</Grid>
            <Grid item xs={12} lg={6}>{renderClassificacao(classificacao.B || [], "Grupo B")}</Grid>
            <Grid item xs={12}>{renderClassificacao(classificacaoGeral, "Classificação geral")}</Grid>
          </Grid>
          {renderGames("Semifinais", semifinais, "Semifinais ainda não cadastradas.")}
          {renderGames("Final", finais, "Final ainda não cadastrada.")}
        </>
      )}
    </AppLayout>
  );
};

export default ClassificacaoPage;
